import { useState, useEffect, useMemo, useRef } from "react"
import { BASEURI, getAuthToken, handleAuthTokenError } from '../../utils';

export default function useSubmissionHttpLogs({ jobId, startTime, endTime, limit = 100, refreshToken } = {}) {
    const [logs, setLogs] = useState([])
    const [loading, setLoading] = useState(Boolean(jobId))
    const [error, setError] = useState(null)
    const [status, setStatus] = useState(jobId ? "Loading..." : "Idle")
    const abortRef = useRef(null);

    const url = useMemo(() => {
        if (!jobId) return null;

        const rawParams = {
            job_id: jobId,
            start_time: startTime?.toISOString(),
            end_time: endTime?.toISOString(),
            limit,
        }

        const params = Object.entries(rawParams).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null) {
                acc[key] = value
            }
            return acc
        }, {})

        const queryString = new URLSearchParams(params).toString()
        return `${BASEURI}/api/dga/submissions/http-logs?${queryString}`
    }, [jobId, startTime, endTime, limit]);


    useEffect(() => {
        const fetchLogs = async (signal) => {
            if (!url) {
                setLogs([])
                setLoading(false)
                setError(null)
                setStatus("Idle")
                return
            }

            setLoading(true)
            setStatus("Loading...")
            setError(null)

            try {
                const token = await getAuthToken().catch(handleAuthTokenError);
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8',
                        ...(token ? { Authorization: `Bearer  ${token}` } : {}),
                    },
                    signal,
                })

                if (!response.ok) {
                    const msg = await response.text().catch(() => response.statusText);
                    throw new Error(msg || `Request failed with status ${response.status}`);
                }

                const data = await response.json()
                if (signal?.aborted) return;
                setLogs(data?.items || [])
                setStatus("Success")
            } catch (err) {
                if (signal?.aborted) return;
                setError(err)
                setStatus("Error")
                setLogs([])
            } finally {
                if (!signal?.aborted) setLoading(false)
            }
        }

        if (abortRef.current) abortRef.current.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        fetchLogs(ac.signal);

        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, refreshToken])

    return { logs, loading, error, status }
}
